import React, { useEffect, useState } from 'react'
import { NewModal } from '../../../Components/New/Modal/NewModal'
import { convertImageToBase64, handleBase64ToImage, isValidName } from '../../../Utils/commonFunctions';
import { getLocalMobileDigits, loadMobileCountries, validateCountryMobile } from '../../../Utils/mobileValidation';
import { ProfileTy } from '../../../Components/AllInput/types';
import { useAppDispatch, useAppSelector } from '../../../ReduxTool/store/hooks';
import { setModalHeaderFooter } from '../../../ReduxTool/Slice/CommonReducers/CommonReducers';
import { ModalId } from '../../../Components/New/Modal/ProfileModal/Const';
import { UserDetailsAction } from '../../../ReduxTool/Slice/Auth/asyncHandlers';
import { toast } from 'react-toastify';
import NewProfileModal from './NewProfileModal';
import { ParsedCountry } from 'react-international-phone';
import { IconSave } from '../../../assests/icons/MapToolsIcons';

function UserProfileModal() {
  const dispatch = useAppDispatch();
  const { user: { address: UserGlobalAddress }, user } = useAppSelector((state) => state.auth);
  const { roofAnalysis: { formDetails: { projectSetup }, selected_project } } = useAppSelector((state) => state.consumerReducers);
  const { supportSubjectList, allDiscom, modal: { modalid, title: modalTitle, btnTxt, secondaryBtnTxt, isheader } } = useAppSelector(state => state.commonReducers)
  const [error, setError] = useState<Record<string, string>>({});

  const [profileLoading, setProfileLoading] = useState<boolean>(false);
  // Profile Form State
  const imageFile = handleBase64ToImage(user?.image ?? "");
  const [viewProfileDetails, setViewProfileDetails] = useState<ProfileTy>({
    fname: user?.fname || "", lname: user?.lname || "", userEmail: user?.emailid || "",
    userMobile: user?.mobile ? `${user.country_mstr?.countrycode}${user.mobile}` : "", countrycode: user?.country_mstr?.countrycode ?? "", isvalid: true,
    profileimage: imageFile
  });

  useEffect(() => {
    setViewProfileDetails({
      fname: user?.fname || "", lname: user?.lname || "", userEmail: user?.emailid || "", userMobile: user?.mobile ? `${user.country_mstr?.countrycode}${user.mobile}` : "",
      countrycode: user?.country_mstr?.countrycode ?? "", isvalid: true, profileimage: handleBase64ToImage(user?.image ?? "")
    })
  }, [modalid, user])


  // --- SCENARIO LOGIC: Toggle Between Profile and Address Modals ---
  const handleModalTransition = (targetModal: "profile" | "address") => {
    if (targetModal === "address") {

      dispatch(setModalHeaderFooter({ title: "Edit Address", btnTxt: "Save", secondaryBtnTxt: "Cancel", modalid: ModalId.UserAddress, isheader: true }));
    } else {

      dispatch(setModalHeaderFooter({ title: "Account Information", btnTxt: "Save", secondaryBtnTxt: "", modalid: ModalId.UserProfileEditMode, isheader: true }));
    }
  };

  const validateProfileFields = async (): Promise<boolean> => {
    const newErrors: Record<string, string> = {};

    if (!viewProfileDetails.fname?.trim()) {
      newErrors.fname = "First name is required.";
    } else if (!isValidName(viewProfileDetails.fname)) {
      newErrors.fname = "First name contains invalid characters.";
    }

    if (!viewProfileDetails.lname?.trim()) {
      newErrors.lname = "Last name is required.";
    } else if (!isValidName(viewProfileDetails.lname)) {
      newErrors.lname = "Last name contains invalid characters.";
    }

    // if (!viewProfileDetails.userEmail?.trim()) {
    //   newErrors.userEmail = "Email is required.";
    // }

    const localPart = getLocalMobileDigits(viewProfileDetails.userMobile ?? "", user.country_mstr?.countrycode);
    try {
      const countries = await loadMobileCountries();
      const mobileCountry = countries.find(country => country.countryid === user.countryid ||
        country.alpha2code === user.country_mstr?.alpha2code);
      const mobileError = validateCountryMobile(localPart, mobileCountry);
      if (mobileError) newErrors.userMobile = mobileError;
    } catch {
      newErrors.userMobile = "Unable to load mobile validation. Please try again.";
    }

    if (Object.keys(newErrors).length > 0) {
      setError(newErrors);
      return false;
    }

    return true;
  };

  const closeModal = () => {
    if (modalid === ModalId.UserProfileEditModeSupport) {
      dispatch(setModalHeaderFooter({ title: "Support", btnTxt: "Send", secondaryBtnTxt: "", isheader: true, modalid: "supportsystem" }));
    } else {
      dispatch(setModalHeaderFooter({ title: "", btnTxt: "", secondaryBtnTxt: "", isheader: false, modalid: "" }));
    }
  };

  const updateUserProfile = async () => {
    if (!(await validateProfileFields())) return;
    setProfileLoading(true)
    let imageString;
    if (viewProfileDetails.profileimage) {
      imageString = await convertImageToBase64(viewProfileDetails.profileimage);
    }

    const reqbody = {
      fname: viewProfileDetails.fname, lname: viewProfileDetails.lname, emailid: viewProfileDetails.userEmail, mobile: getLocalMobileDigits(viewProfileDetails?.userMobile ?? "", user.country_mstr?.countrycode),
      image: imageString, // address: addressStringToSave, // Pushing the strictly formatted string back
    };

    try {
      const Response = await dispatch(UserDetailsAction({ reqBody: reqbody, userid: user.userid }));

      if (Response?.meta?.requestStatus === "fulfilled") {

        //    setProfileModals((prev) => ({ ...prev, view: true, success: true, message: "Profile updated successfully." }));
        setError({});
        toast.success(`User details updated successfully`, { autoClose: 7000 });
      }

    } catch (error) {
      toast.error(`Failed to update user details.`, { autoClose: 7000 });
    } finally {
      setProfileLoading(false);
      closeModal()
    }
  };

  const handleChange = (e: any, statename: string, selectedOption?: any) => {
    let { name, value } = e?.target ?? e;
    if (selectedOption) { name = selectedOption.name; value = e; }

    let valueErr = false;
    if ((name === "fname" || name === "lname") && (value.length > 20 || value === " " || !isValidName(value))) {
      valueErr = true;
      e.preventDefault?.();
    }

    if (!valueErr || value === "") {
      if (statename === "profile") setViewProfileDetails((prev) => ({ ...prev, [name]: value }));

      if (error && error[name as keyof typeof error]) {
        const newError = { ...error };
        delete newError[name as keyof typeof newError];
        setError(newError);
      }
    }
  };

  // --- Input Handlers ---
  const handleMobileNumber = async (phone: string, country: ParsedCountry) => {
    let formattedPhone = phone;
    if (!formattedPhone.startsWith(viewProfileDetails.countrycode)) {
      if (formattedPhone.startsWith('+')) formattedPhone = formattedPhone.replace(/^\+\d*/, '');
      formattedPhone = `${viewProfileDetails.countrycode}${formattedPhone}`;
    }
    const isValid = !validateCountryMobile(getLocalMobileDigits(formattedPhone, user.country_mstr?.countrycode), user.country_mstr);
    setViewProfileDetails((prev) => ({ ...prev, userMobile: formattedPhone, isvalid: isValid }));

    if (error && error["userMobile" as keyof typeof error]) {
      const newError = { ...error };
      delete newError["userMobile" as keyof typeof newError];
      setError(newError);
    }
  };

  const handleViewProfileUpdate = (title: string) => {
    updateUserProfile();
  };
  return (
    (<NewModal isLoading={profileLoading} isAbleCLick onClick={handleViewProfileUpdate} setIsCLose={() => closeModal()}
      name={"Profile Details"} modalSize="lg" btnName="profile" maxheight={"fit-content"} primarylefticon={<IconSave />} >
      <NewProfileModal isEditing={false} handleMobileNumber={handleMobileNumber} setProfileState={setViewProfileDetails} openEditAddres={() => handleModalTransition("address")}
        error={error} profileState={viewProfileDetails} handleChange={(e: any) => handleChange(e, "profile")} />
    </NewModal>
    )
  )
}

export default UserProfileModal
